import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Image, Form, Button } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import Card from "../../../components/Card";
import logo from "../../../assets/images/coyote-logo.jpg";
import auth1 from "../../../assets/images/auth/01.png";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import InputField from "../../../components/input/input-field";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import { useDispatch } from "react-redux";
import {
  fetchPermissionsData,
  fetchRolesData,
} from "../../../store/setting/actions";

const SignIn = () => {
  const alertRef = useRef(null);
  const location = useLocation();
  const [status, setStatus] = useState(location.state?.status || "");
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "expired") {
      setAlert({
        message: "Your session is expired, please login again",
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } else if (status === "invalid-token") {
      setAlert({
        message: "Invalid token. Please login again",
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  }, [status]);

  useEffect(() => {
    if (location.state?.status) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleResend = async (email) => {
    try {
      setAlert({ show: false, alertFor: "", message: "", type: "" });
      const res = await axios.post(
        `${url.backendURL}user/resend-verification`,
        { email },
      );
      setAlert({
        message: res.data.message,
        type: "success",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      let errorMessage = err.response
        ? err.response.data.message
        : "An error occurred";

      if (
        err.response &&
        err.response.data.error &&
        err.response.data.error.includes("To prevent spam")
      ) {
        const remainingMinutes =
          err.response.data.remainingMinutes ||
          err.response.data.response?.data?.remainingMinutes ||
          0;
        
        const hours = Math.floor(remainingMinutes / 60);
        const mins = remainingMinutes % 60;
        const parts = [];
        
        if (hours > 0) {
          parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
        }
        if (mins > 0) {
          parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
        }
        
        const timeDisplay = parts.join(" and ") || "a few seconds";

        errorMessage = (
          <span>
            To prevent spam, we only resend links every 3 hours. You can try
            again in <b>{timeDisplay}</b>
          </span>
        );
      }

      setAlert({
        message: errorMessage,
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${url.backendURL}user/login`, {
        email: values.email,
        password: values.password,
      });
      localStorage.setItem("coyote-token", response.data.response.jwt);
      setAlert({ show: false, alertFor: "", message: "", type: "" });
      dispatch(fetchPermissionsData());
      dispatch(fetchRolesData());
      navigate("/dashboard");
    } catch (err) {
      const { response } = err;
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      let errorMessage = response ? response.data.message : "An error occurred";

      if (
        response &&
        response.data.error &&
        response.data.error.includes("CLICK_HERE to resend")
      ) {
        errorMessage = (
          <span>
            {`Account Not Verified. It looks like you haven't clicked the link in your verification email yet. Didn't get the email?`}{" "}
            <a
              onClick={() => handleResend(values.email)}
              style={{ cursor: "pointer", textDecoration: "underline" }}
            >
              Click here
            </a>{" "}
            to resend
          </span>
        );
      }

      setAlert({
        message: errorMessage,
        type: "danger",
        show: true,
        alertFor: "signin",
      });

      if (
        !(
          response &&
          response.data.error &&
          response.data.error.includes("CLICK_HERE to resend")
        )
      ) {
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Email is required")
      .email("Enter a valid email address")
      .test(
        "no-spaces",
        "Email may not contain any spaces at the beginning or end",
        (value) => !value || value.trim() === value,
      )
      .test("min-domain-segments", "Enter a valid email address", (value) => {
        if (!value) return true;
        const parts = value.split("@");
        if (parts.length !== 2) return false;
        const domainSegments = parts[1].split(".");
        return (
          domainSegments.length >= 2 &&
          domainSegments.every((s) => s.length >= 2)
        );
      })
      .test("realistic-email", "Enter a valid email address", (value) => {
        if (!value) return true;
        const [local, domain] = value.split("@");
        if (!local || !domain) return false;

        if (/^\d+$/.test(local)) return false;
        if (local.length > 64) return false;

        if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

        return true;
      }),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters long")
      .max(30, "Password cannot be longer than 30 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{6,}$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
      )
      .required("Password is required"),
  });

  return (
    <section className="login-content">
      <Row className="m-0 align-items-center bg-white vh-100">
        <Col md="6">
          <Row className="justify-content-center">
            <div className="d-flex justify-content-between flex-column text-center align-items-center">
              <Link
                to={`/user/auth/sign-in`}
                className="navbar-brand d-flex align-items-center text-center h-50 w-50"
              >
                <img
                  className="logo-title text-center coyote-logo-image h-100 w-100"
                  src={logo}
                  alt="coyote-logo"
                />
              </Link>
            </div>
            <Col md="10" ref={alertRef}>
              <Card className="card-transparent shadow-none d-flex justify-content-center mb-0 auth-card">
                <Card.Body>
                  <AlertNotificationSimple
                    type={alert.type}
                    message={alert.message}
                    show={alert.show}
                    onClose={() => setAlert({ ...alert, show: false })}
                  />
                  <h2 className="mb-2 d-flex align-items-center justify-content-center text-center">
                    Sign In
                  </h2>
                  <Formik
                    initialValues={{
                      email: "",
                      password: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <FocusError />
                        <Row>
                          <Col lg="12" style={{ zIndex: "1" }}>
                            <InputField
                              className="form-group"
                              type="email"
                              name="email"
                              placeholder="example@email.com"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              errorMessage={
                                alert?.message ===
                                "Email is not allowed to be empty"
                                  ? [alert.message]
                                  : []
                              }
                              label="Email"
                            />
                          </Col>
                          <Col lg="12">
                            <InputField
                              className="form-group"
                              type="password"
                              name="password"
                              placeholder="Enter your password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showPasswordToggle={true}
                              showPassword={showPassword}
                              setShowPassword={setShowPassword}
                              asterick={true}
                              errorMessage={
                                alert?.message ===
                                "Password is not allowed to be empty"
                                  ? [alert.message]
                                  : []
                              }
                              label="Password"
                            />
                          </Col>
                          <Col
                            lg="12"
                            className="d-flex justify-content-between"
                          >
                            <Form.Check className="form-check mb-3">
                              <Form.Check.Input
                                type="checkbox"
                                id="customCheck1"
                              />
                              <Form.Check.Label htmlFor="customCheck1">
                                Remember Me
                              </Form.Check.Label>
                            </Form.Check>
                            <Link to="/user/auth/forgot-password">
                              Forgot Password?
                            </Link>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-center">
                          <Button
                            type="submit"
                            variant="btn btn-primary"
                            disabled={isSubmitting}
                          >
                            Sign In
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div className="sign-bg">
            <svg
              width="280"
              height="230"
              viewBox="0 0 431 398"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.05">
                <rect
                  x="-157.085"
                  y="193.773"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(-45 -157.085 193.773)"
                  fill="#3B8AFF"
                />
                <rect
                  x="7.46875"
                  y="358.327"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(-45 7.46875 358.327)"
                  fill="#3B8AFF"
                />
                <rect
                  x="61.9355"
                  y="138.545"
                  width="310.286"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(45 61.9355 138.545)"
                  fill="#3B8AFF"
                />
                <rect
                  x="62.3154"
                  y="-190.173"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(45 62.3154 -190.173)"
                  fill="#3B8AFF"
                />
              </g>
            </svg>
          </div>
        </Col>
        <Col
          md="6"
          className="d-md-block d-none bg-primary p-0 mt-n1 vh-100 overflow-hidden"
        >
          <Image
            src={auth1}
            className="Image-fluid gradient-main animated-scaleX"
            alt="images"
          />
        </Col>
      </Row>
    </section>
  );
};

export default SignIn;
